import PropTypes from 'prop-types';
import FileDropzone from '../common/fileUpload/FileDropzone';
import { formatFileSize } from '../../helpers/fileValidationUtils';

// The BoQ is a pricing schedule — spreadsheets, CSV exports or PDF bills only
const BOQ_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
};

const FileIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const CrossIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const UploadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 16V5" />
    <path d="M6 10l6-6 6 6" />
    <path d="M4 20h16" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12l5 5L20 7" />
  </svg>
);

const PricingBasisSection = ({
  boqFile,
  onDropAccepted,
  onDropRejected,
  onRemove,
  dropError,
}) => {
  const boqAdded =
    !!boqFile &&
    (!Array.isArray(boqFile.errors) || boqFile.errors.length === 0);

  return (
    <div>
      <div className="um-sec-head">
        <div className="um-sec-head-main">
          <h2 className="um-sec-title">Pricing basis</h2>
          <p className="um-sec-sub">
            Upload the BoQ or pricing schedule against which quotes will be
            compared.
          </p>
        </div>
        {boqAdded ? (
          <span className="um-chip tone-success">
            <CheckIcon />
            Added
          </span>
        ) : (
          <span className="um-chip tone-info">Optional</span>
        )}
      </div>
      <div className="um-card">
        {boqFile ? (
          <>
            <div className={`um-boq-row ${boqAdded ? 'added' : ''}`}>
              <span className="um-boq-icon">
                <FileIcon />
              </span>
              <div className="um-boq-main">
                <div className="um-boq-name" title={boqFile.file.path}>
                  {boqFile.file.path}
                </div>
                <div className="um-boq-meta">
                  {formatFileSize(boqFile.rawFile?.size || 0)} · Bill of
                  Quantities
                </div>
              </div>
              <div className="um-boq-actions">
                <FileDropzone
                  onDrop={onDropAccepted}
                  onDropRejected={onDropRejected}
                  accept={BOQ_FILE_TYPES}
                  multiple={false}
                  maxFiles={1}
                  baseClassName="um-boq-replace"
                  ariaLabel="Replace pricing document"
                >
                  {() => <>Replace</>}
                </FileDropzone>
                <button
                  type="button"
                  className="um-icon-btn"
                  aria-label="Remove pricing document"
                  onClick={onRemove}
                >
                  <CrossIcon />
                </button>
              </div>
            </div>
            {Array.isArray(boqFile.errors) && boqFile.errors.length > 0 && (
              <p className="um-field-error">{boqFile.errors.join(', ')}</p>
            )}
          </>
        ) : (
          <FileDropzone
            onDrop={onDropAccepted}
            onDropRejected={onDropRejected}
            accept={BOQ_FILE_TYPES}
            multiple={false}
            maxFiles={1}
            baseClassName="um-drop"
            ariaLabel="Drop the BoQ or pricing schedule here"
          >
            {({ isDragActive }) => (
              <>
                <UploadIcon />
                <span className="um-drop-main">
                  {isDragActive ? (
                    'Drop the file here…'
                  ) : (
                    <>
                      Drop the BoQ or pricing schedule here, or{' '}
                      <span className="um-browse">browse</span>
                    </>
                  )}
                </span>
                <span className="um-drop-hint">
                  XLSX, XLS, CSV or PDF · One file per package
                </span>
              </>
            )}
          </FileDropzone>
        )}
        {dropError && (
          <p className="um-drop-reject" role="alert">
            {dropError}
          </p>
        )}
      </div>
    </div>
  );
};

PricingBasisSection.propTypes = {
  boqFile: PropTypes.shape({
    file: PropTypes.shape({ path: PropTypes.string }),
    rawFile: PropTypes.object,
    errors: PropTypes.array,
  }),
  onDropAccepted: PropTypes.func.isRequired,
  onDropRejected: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  dropError: PropTypes.string,
};

export default PricingBasisSection;
